import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import { alsHtml, alsText, bereinige } from '../../shared/richText'

import styles from './style.module.scss'

/**
 * Die vier Auszeichnungen, mehr nicht.
 *
 * Bewusst kurz gehalten: Überschriften, Farben und Schriftgrößen in einem
 * Kommentarfeld führen zu Beiträgen, die aussehen wie ein Faltblatt und sich
 * lesen wie eines. Was hier steht, deckt ab, wofür Leute sonst Sternchen
 * tippen — hervorheben, betonen, aufzählen.
 */
const WERKZEUGE = [
	{ befehl: 'bold', titel: 'Fett (Strg+B)', label: 'F', klasse: 'toolBold' },
	{ befehl: 'italic', titel: 'Kursiv (Strg+I)', label: 'K', klasse: 'toolItalic' },
	{ befehl: 'underline', titel: 'Unterstrichen (Strg+U)', label: 'U', klasse: 'toolUnderline' },
	{ befehl: 'insertUnorderedList', titel: 'Aufzählung', label: '•—', klasse: 'toolList' },
	{ befehl: 'insertOrderedList', titel: 'Nummerierte Liste', label: '1.', klasse: 'toolList' }
]

/**
 * Ein Eingabefeld mit den vier Grundauszeichnungen.
 *
 * `contentEditable` statt eines Editor-Pakets: Was hier gebraucht wird — fett,
 * kursiv, unterstrichen, Aufzählung — kann der Browser selbst, und zwar
 * einschließlich der Tastenkürzel und der Schreibmarken-Logik, an der ein
 * selbstgebauter Editor als Erstes scheitert. Ein Paket dafür hieße: eine
 * Abhängigkeit mehr im Bündel, ein eigenes Datenformat und eine zweite Stelle,
 * an der Eingaben gereinigt werden müssen.
 *
 * **Das Feld ist bewusst unkontrolliert.** Bei jedem Tastendruck das `innerHTML`
 * neu zu setzen, würde die Schreibmarke ans Ende werfen — nach dem ersten
 * Buchstaben ließe sich nichts mehr in der Mitte eines Satzes ändern. Der Wert
 * von außen wird deshalb nur dann hineingeschrieben, wenn er von dem abweicht,
 * was zuletzt von hier kam: beim ersten Anzeigen und wenn der Aufrufer das Feld
 * leert (nach dem Senden).
 */
export function RichTextEditor(props) {
	const { value, onChange, placeholder, className, disabled, ariaLabel, onKeyDown, onPaste, flach } =
		props

	const feld = useRef(null)
	// Was zuletzt von hier nach außen ging. Der Vergleich damit entscheidet, ob
	// ein neuer Wert von außen wirklich neu ist oder nur das Echo der eigenen
	// Eingabe.
	const eigenerStand = useRef(null)
	const [leer, setLeer] = useState(true)
	const [aktiv, setAktiv] = useState({})

	useEffect(() => {
		const el = feld.current
		if (!el) return

		const html = alsHtml(value)
		if (html !== eigenerStand.current) {
			el.innerHTML = html
			eigenerStand.current = html
		}
		setLeer(!alsText(value).trim())
	}, [value])

	const melde = useCallback(() => {
		const el = feld.current
		if (!el) return

		const html = bereinige(el.innerHTML)
		eigenerStand.current = html
		setLeer(!alsText(html).trim())
		onChange(html)
	}, [onChange])

	/*
	 * `document.execCommand` ist als veraltet gekennzeichnet und wird trotzdem
	 * von jedem Browser unterstützt — einen Nachfolger gibt es nicht. Der
	 * Gegenentwurf wäre, Auswahlbereiche von Hand zu zerlegen und wieder
	 * zusammenzusetzen; das ist genau die Arbeit, die hier niemand machen will.
	 *
	 * `styleWithCSS = false` sorgt dafür, dass `<b>` und `<i>` herauskommen und
	 * nicht `<span style="font-weight:bold">` — Letzteres würde die Reinigung
	 * verwerfen, und die Auszeichnung wäre beim nächsten Anzeigen weg.
	 */
	const fuehreAus = befehl => {
		const el = feld.current
		if (!el || disabled) return

		el.focus()
		document.execCommand('styleWithCSS', false, false)
		document.execCommand(befehl, false, null)
		melde()
		leseZustand()
	}

	// Welche Auszeichnung gerade an der Schreibmarke gilt — damit die Knöpfe
	// zeigen, worin man schreibt, statt nur, was sie können.
	const leseZustand = () => {
		if (typeof document.queryCommandState !== 'function') return
		const stand = {}
		for (const werkzeug of WERKZEUGE) {
			try {
				stand[werkzeug.befehl] = document.queryCommandState(werkzeug.befehl)
			} catch (error) {
				stand[werkzeug.befehl] = false
			}
		}
		setAktiv(stand)
	}

	return (
		<div
			className={clsx(
				styles.root,
				flach && styles.rootFlat,
				disabled && styles.rootDisabled,
				className
			)}
		>
			<div className={styles.toolbar} role="toolbar" aria-label="Textauszeichnung">
				{WERKZEUGE.map(werkzeug => (
					<button
						key={werkzeug.befehl}
						type="button"
						className={clsx(
							styles.tool,
							styles[werkzeug.klasse],
							aktiv[werkzeug.befehl] && styles.toolActive
						)}
						title={werkzeug.titel}
						aria-label={werkzeug.titel}
						aria-pressed={Boolean(aktiv[werkzeug.befehl])}
						disabled={disabled}
						// Der Knopf darf die Auswahl im Feld nicht verlieren —
						// sonst weiß der Befehl nicht mehr, worauf er wirken soll.
						onMouseDown={event => event.preventDefault()}
						onClick={() => fuehreAus(werkzeug.befehl)}
					>
						{werkzeug.label}
					</button>
				))}
			</div>

			<div className={styles.area}>
				{/*
				 * Der Platzhalter ist ein eigenes Element und kein
				 * `::before`-Inhalt am Feld: An einem `contentEditable` würde er
				 * je nach Browser mit ausgewählt und mitkopiert.
				 */}
				{leer && !disabled && <span className={styles.placeholder}>{placeholder}</span>}
				<div
					ref={feld}
					className={styles.input}
					contentEditable={!disabled}
					suppressContentEditableWarning
					role="textbox"
					aria-multiline="true"
					aria-label={ariaLabel || placeholder}
					onInput={melde}
					onKeyUp={leseZustand}
					onMouseUp={leseZustand}
					onFocus={leseZustand}
					onBlur={() => setAktiv({})}
					onKeyDown={onKeyDown}
					onPaste={event => {
						if (onPaste) {
							onPaste(event)
							if (event.defaultPrevented) return
						}

						/*
						 * Eingefügt wird reiner Text. Aus Word oder einer
						 * Webseite käme sonst deren komplettes Gerüst herein —
						 * die Reinigung würde das meiste wegwerfen, aber das
						 * Ergebnis wäre für die Schreibende nicht vorhersehbar.
						 * Wer etwas hervorheben will, tut es hier.
						 */
						event.preventDefault()
						const text = event.clipboardData?.getData('text/plain') || ''
						document.execCommand('insertText', false, text)
					}}
				/>
			</div>
		</div>
	)
}

RichTextEditor.propTypes = {
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
	placeholder: PropTypes.string,
	className: PropTypes.string,
	disabled: PropTypes.bool,
	ariaLabel: PropTypes.string,
	onKeyDown: PropTypes.func,
	onPaste: PropTypes.func,
	// Ohne eigenen Rahmen — fuer Felder, die schon in einem Kasten stehen
	// (das Kommentarfeld liegt in der Ablageflaeche fuer Anhaenge). Zwei
	// geschachtelte Raender saehen aus wie zwei Felder.
	flach: PropTypes.bool
}

RichTextEditor.defaultProps = {
	value: '',
	placeholder: '',
	className: '',
	disabled: false,
	ariaLabel: '',
	onKeyDown: undefined,
	onPaste: undefined,
	flach: false
}

/**
 * Ausgezeichneten Text anzeigen.
 *
 * Gereinigt wird hier, beim Anzeigen — nicht (nur) beim Speichern. Was in der
 * Datenbank steht, ist damit einerlei; und alter, reiner Text sieht aus wie
 * bisher, weil `alsHtml` seine Zeilenumbrüche selbst übernimmt.
 */
export function RichTextView({ value, className }) {
	const html = alsHtml(value)
	if (!html) return null

	// eslint-disable-next-line react/no-danger
	return <div className={clsx(styles.view, className)} dangerouslySetInnerHTML={{ __html: html }} />
}

RichTextView.propTypes = {
	value: PropTypes.string,
	className: PropTypes.string
}

RichTextView.defaultProps = {
	value: '',
	className: ''
}
